import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const stats = [
  { value: '12+', label: '收录平台', icon: 'ri-stack-line' },
  { value: '50+', label: '深度文章', icon: 'ri-article-line' },
  { value: '100K+', label: '月访问用户', icon: 'ri-user-line' },
  { value: '98%', label: '用户好评率', icon: 'ri-heart-line' },
];

const values = [
  {
    title: '客观公正',
    description: '我们坚持独立评测，不接受平台付费推广。每一条推荐都基于真实的使用体验和用户反馈，确保推荐结果的客观性和公正性。',
    icon: 'ri-scales-line',
  },
  {
    title: '深度专业',
    description: '团队由经验丰富的创作者和内容运营专家组成，从创作者视角出发，提供具有实操价值的平台分析和创作指南。',
    icon: 'ri-brain-line',
  },
  {
    title: '持续更新',
    description: '内容创作生态日新月异，我们持续追踪各平台的最新动态、政策变化和功能更新，确保推荐信息始终准确有效。',
    icon: 'ri-refresh-line',
  },
  {
    title: '用户至上',
    description: '以创作者需求为核心，从内容类型、变现能力、成长空间等多维度筛选平台，帮助每一位创作者找到最适合的创作家园。',
    icon: 'ri-user-heart-line',
  },
];

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const resp = await fetch('https://readdy.ai/api/form/d9sr0mp4ne17ei0sdmfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (resp.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormState('error');
      }
    } catch {
      setFormError('网络异常，请稍后重试');
      setFormState('error');
    }
  };

  const seoTitle = '关于我们 - Po文导航 | 原创内容发布平台推荐';
  const seoDescription = 'Po文导航致力于为中文创作者提供最优质的po文网站推荐服务。了解我们的品牌故事、核心价值和联系方式，一起探索内容创作的无限可能。';
  const seoKeywords = 'po文网站,关于我们,创作者平台,内容发布平台推荐,Po文导航';

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于Po文导航',
    description: seoDescription,
    about: {
      '@type': 'Organization',
      name: 'Po文导航',
      description: '原创内容发布平台推荐与创作指南',
    },
  };

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://powen.example.com/about"
        schema={aboutSchema}
      />
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
            <h1 className="mt-3 text-2xl md:text-4xl font-bold font-heading text-foreground-900">
              关于 Po文导航
            </h1>
            <p className="mt-2 text-foreground-500 max-w-2xl">
              致力于为中文创作者提供最优质的原创内容发布平台推荐服务
            </p>
          </div>
        </div>

        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  我们的品牌故事
                </h2>
                <p className="mt-4 text-foreground-600 leading-relaxed">
                  Po文导航诞生于2024年，由一群热爱内容创作的写作者和产品人共同打造。
                  我们发现，很多新人创作者面对琳琅满目的发布平台时，
                  常常感到迷茫——不知道哪个平台适合自己，不了解各平台的规则和特点，
                  更不清楚如何高效地实现内容变现。
                </p>
                <p className="mt-3 text-foreground-600 leading-relaxed">
                  于是，我们决定建立一个专业的<strong>po文网站</strong>推荐平台，
                  通过客观评测、深度分析和实用指南，帮助每一位创作者找到最适合自己的创作家园。
                  从注册教程到流量变现，从平台对比到避坑指南，
                  我们希望成为创作者路上的忠实伙伴。
                </p>
                <p className="mt-3 text-foreground-600 leading-relaxed">
                  两年来，我们已经累计帮助超过10万名创作者找到了适合自己的发布平台，
                  收获了98%的用户好评率。未来，我们将继续深耕内容创作领域，
                  为更多创作者提供有价值的服务。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20creative%20team%20workspace%2C%20people%20collaborating%20around%20a%20wooden%20table%2C%20natural%20light%20from%20large%20windows%2C%20plants%20and%20books%20in%20background%2C%20modern%20cozy%20office%2C%20cream%20and%20terracotta%20color%20scheme%2C%20editorial%20photography%20style&width=800&height=600&seq=about-brand-story&orientation=landscape"
                  alt="Po文导航团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center">
              核心价值
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="bg-background-50 border border-background-200 rounded-xl p-6"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                    <i className={`${v.icon} text-lg text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center">
              用户口碑
            </h2>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-50 flex items-center justify-center">
                    <i className={`${stat.icon} text-lg text-accent-600`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                    {stat.value}
                  </div>
                  <div className="mt-1 text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '小雨',
                  role: '网络小说作者',
                  text: '在Po文导航的帮助下，我从对平台一窍不通的新人，成长为在起点签约的作家。平台的评测很客观，帮我少走了很多弯路。',
                },
                {
                  name: '阿杰',
                  role: '自媒体博主',
                  text: '通过Po文导航的推荐，我找到了最适合自己的平台组合。现在同时运营公众号和小红书，月收入稳定在五位数。感谢有这样一个专业的平台推荐。',
                },
                {
                  name: '思涵',
                  role: '自由撰稿人',
                  text: '最打动我的是他们的客观态度——不收平台广告费，只推荐真正好用的平台。每篇文章都很用心，干货满满，是创作者的宝藏网站。',
                },
              ].map((review) => (
                <div
                  key={review.name}
                  className="bg-background-100 border border-background-200 rounded-xl p-6"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className="ri-star-fill text-sm text-accent-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div className="mt-4 pt-4 border-t border-background-200">
                    <p className="text-sm font-semibold text-foreground-800">{review.name}</p>
                    <p className="text-xs text-foreground-400">{review.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center">
              联系我们
            </h2>
            <p className="mt-3 text-foreground-500 text-center">
              有任何问题、建议或合作意向？欢迎通过以下表单与我们联系
            </p>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-8 space-y-5"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 text-sm border border-background-300 rounded-lg bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 text-sm border border-background-300 rounded-lg bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  placeholder="请输入您的邮箱地址"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 text-sm border border-background-300 rounded-lg bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">限500字以内</p>
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <button
                type="submit"
                disabled={formState === 'submitting'}
                className="w-full py-3.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                {formState === 'submitting' ? '提交中...' : '发送留言'}
              </button>

              {formState === 'success' && (
                <p className="text-sm text-center text-accent-700 bg-accent-50 rounded-lg py-3 px-4">
                  提交成功！感谢您的留言，我们会尽快回复。
                </p>
              )}
              {formState === 'error' && formError && (
                <p className="text-sm text-center text-red-600 bg-red-50 rounded-lg py-3 px-4">
                  {formError}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}